// TODO: rename StudyLocationsDetails
import React from "react"
import { useParams } from "react-router-dom"

// Components
import Checkbox from "components/Checkbox/Checkbox"
import useSiteUploadActions from "components/useSiteUploadActions/useSiteUploadActions"
import { PrimeButton, PRSStatusBadge } from "components-design-system"
import { Flex, Dropdown, Divider, theme, Typography } from "antd"
import PRSValidationBadge from "components/badges/PRSValidationBadge/PRSValidationBadge"
import PRSValidationMessages from "components/PRSValidationMessages/PRSValidationMessages"
import Modal from "components/Modal/Modal"
import "./useStudyDetailsHeader.scss"

// helpers
import { intersperse } from "utilities/misc"

// apis
import { useUserPermissions } from "api/hooks"
import {
  useGetPrsSitesUpload,
  usePostPrsSiteUpload,
  usePutSiteManagementMasking,
} from "api/hooks/siteManagement/useSiteManagementApi"
import { useGetStudy } from "api/hooks/studies/useStudiesApi"

const { useToken } = theme
const { Text } = Typography

const useStudyDetailsHeader = ({
  isUploadDisabled,
  onRegenerate,
  setTriggerExport,
  nonSiteErrors,
  siteManagementStudy,
  getValidationsAfterUpload,
  siteManagementMaskingSetting,
  setSiteManagementMaskingSetting,
}) => {
  const { token } = useToken()
  const { studyId, siteManagementId } = useParams()
  const [badges, setBadges] = React.useState([])
  const [openModal, setOpenModal] = React.useState(false)
  const { data: permissions } = useUserPermissions()
  const { data: study } = useGetStudy(studyId)
  const studyData = study?.study_data
  const putSiteManagementMasking = usePutSiteManagementMasking(siteManagementId)

  React.useEffect(() => {
    let items = []
    if (siteManagementStudy) {
      if (siteManagementStudy.is_new) {
        items.push(
          <PRSStatusBadge
            key="header-new-badge"
            variant="new"
            tooltipPlacement="top"
          />
        )
      }
      if (siteManagementStudy.only_on_ctg) {
        items.push(
          <PRSStatusBadge
            key="header-no-source"
            variant="no-source"
            tooltipPlacement="top"
          />
        )
      }
      if (siteManagementStudy.only_on_source) {
        items.push(
          <PRSStatusBadge
            key="header-no-ctg"
            diagnolStyle={{ bottom: 11, width: 45, right: -2 }}
            variant="no-ctg"
            tooltipPlacement="top"
          />
        )
      }
      setBadges(items)
    }
  }, [siteManagementStudy, isUploadDisabled])

  const displayNonSiteErrors = () => {
    let errors = []
    let warnings = []
    let notes = []
    if (nonSiteErrors?.length > 0) {
      nonSiteErrors.forEach((item) => {
        if (item.severity === "ERROR" || item.type === "ERROR") errors.push(item)
        else if (item.severity === "WARNING" || item.type === "WARNING") warnings.push(item)
        else notes.push(item)
      })

      let items = []

      if (errors.length > 0) {
        items.push(
          <PRSValidationBadge
            key={"sl-non-sites"}
            // style={{ marginLeft: 4 }}
            tooltipType={"non-site"}
            num={errors.length}
            type={"error"}
          />
        )
      }
      if (warnings.length > 0) {
        items.push(
          <PRSValidationBadge
            key={"sl-warnings"}
            // style={{ marginLeft: 4 }}
            tooltipType={"warning"}
            num={warnings.length}
            type={"warning"}
          />
        )
      }
      if (notes.length > 0) {
        items.push(
          <PRSValidationBadge
            key={"sl-notes"}
            // style={{ marginLeft: 4 }}
            tooltipType={"note"}
            num={notes.length}
            type={"note"}
          />
        )
      }

      return (
        <Flex
          gap={4}
          vertical={true}
        >
          <Text strong>Non-Site PRS Validation messages</Text>
          <Flex
            style={{ cursor: "pointer" }}
            gap={4}
            onClick={() => setOpenModal(true)}
            justify="flex-end"
          >
            {intersperse(items, ", ")}
          </Flex>
        </Flex>
      )
    }
  }

  const canUploadToPRS = permissions?.["study.site.upload"]

  const {
    RegenerateButton,
    RegenerateIconButton,
    UploadButton,
    CancelButton,
    ValidateButton,
    ExportButton,
    isUploading,
    UploadModals,
  } = useSiteUploadActions({
    prsUploadStudyList: [Number(siteManagementId)],
    useOnGetPrsUpload: useGetPrsSitesUpload,
    useOnPostPrsUpload: usePostPrsSiteUpload,
    uploadToPrsMsg:
      "Send all site updates to PRS. If you would like to release data directly to CTG check the checkbox below.",
    showExport: true,
    showValidate: true,
    setTriggerExport: setTriggerExport,
    canUploadToPRS,
    studyData: { ...studyData, authoringId: study?.authoring_study_id },
    onRegenerate: onRegenerate,
    isUploadDisabled: isUploadDisabled,
    metadata: {},
    source: "site-management-study",
    lastImportDate: siteManagementStudy?.ctg_download_date,
    callbackAfterUpload: getValidationsAfterUpload,
  })

  const onSiteManagementMaskingChange = (key, e) => {
    let val = e.target.checked

    let maskingConfig = {
      ...siteManagementMaskingSetting,
      [key]: val,
    }

    setSiteManagementMaskingSetting(maskingConfig)

    const formatMaskingReq = (req) => {
      let resp = {
        contact_mask: req.contact_mask,
        contact_backup_mask: req.contact_mask,
        facility_mask: req.facility_mask,
        investigator_mask: req.investigator_mask,
      }

      return { masking_config: resp }
    }

    let reqBody = formatMaskingReq(maskingConfig)

    putSiteManagementMasking({
      id: `${siteManagementId}/mask-config/`,
      body: reqBody,
    })
  }

  const contentStyle = {
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
  }
  const menuStyle = {
    boxShadow: "none",
    marginLeft: "auto",
  }

  const items = React.useMemo(() => {
    const ret = [
      {
        key: "export",
        label: "Export",
        onClick: () => setTriggerExport(true),
      },
    ]

    return ret
  }, [siteManagementMaskingSetting, permissions])

  if (!study) {
    return null
  }

  const SiteMenu = (
    <Dropdown
      menu={{ items }}
      placement="bottomRight"
      className="sites-menu-dropdown"
      dropdownRender={(menu) => (
        <div style={contentStyle}>
          {React.cloneElement(menu, {
            style: menuStyle,
          })}
          <Divider
            style={{
              margin: 0,
            }}
          />
          <Flex
            vertical={true}
            style={{
              padding: 8,
              width: 170,
            }}
            gap={8}
          >
            <Checkbox
              style={{ margin: 0 }}
              checked={siteManagementMaskingSetting?.facility_mask}
              onChange={(e) => onSiteManagementMaskingChange("facility_mask", e)}
              label="Mask Address in CTG Submission"
            />
            <Checkbox
              style={{ margin: 0 }}
              checked={siteManagementMaskingSetting?.contact_mask}
              onChange={(e) => onSiteManagementMaskingChange("contact_mask", e)}
              label="Mask Contact in CTG Submission"
            />
            <Checkbox
              style={{ margin: 0 }}
              checked={siteManagementMaskingSetting?.investigator_mask}
              onChange={(e) => onSiteManagementMaskingChange("investigator_mask", e)}
              label="Mask Investigators in CTG Submission"
            />
          </Flex>
        </div>
      )}
    >
      <PrimeButton icon={<i className="fal fa-ellipsis-v" />} />
    </Dropdown>
  )

  const PRSValidationModal = (
    <Modal
      title={"Non-Site PRS Validation Messages"}
      onCancel={() => setOpenModal(false)}
      open={openModal}
      hideImage={true}
      footer={[]}
      width={720}
    >
      <PRSValidationMessages
        gridProps={{ data: null }}
        data={studyData}
        messages={nonSiteErrors}
        onOpenModal={() => setOpenModal(true)}
        setOpenModal={setOpenModal}
        origin={"locations"}
        hideSubmit={true}
        hideActions={true}
      />
    </Modal>
  )
  const StudyDetailsHeader = (
    <>
      <Flex
        gap={6}
        justify="flex-end"
      >
        {RegenerateButton}
        {canUploadToPRS && UploadButton}
        {SiteMenu}
      </Flex>
      {badges?.length > 0 && <div className="study-loc-badges">{badges}</div>}
      {nonSiteErrors?.length > 0 && (
        <div style={{ display: "flex" }}>
          <span className="study-loc-page-desc">Non-Site PRS Validation messages: </span>
          {displayNonSiteErrors()}
        </div>
      )}
      {PRSValidationModal}
    </>
  )

  return {
    StudyDetailsHeader,
    UploadButton,
    RegenerateButton,
    SiteMenu,
    ValidateButton,
    ExportButton,
    isUploading,
    CancelButton,
    RegenerateIconButton,
    badges,
    displayNonSiteErrors,
    PRSValidationModal,
    UploadModals,
  }
}

export default useStudyDetailsHeader
